import { Flash, MouseCircle, VideoHorizontal } from 'iconsax-react';
import { Cell, Pie, PieChart } from 'recharts';

import { Card, CardContent, CardHeader } from '@src/design-system/card';
import { Text } from '@src/design-system/text';
import { ChartContainer, ChartTooltip } from '@src/design-system/chart';
import { ChartTooltipContent } from '@src/features/dashboard/components/chart-tooltip-content';
import SkeletonLoader from '@src/shared/components/reusables/skeleton';
import { useGetOrgId } from '@shared/hooks/use-get-org-id';
import type { TAnnouncementStatsBreakdownItem } from '@src/shared/types/announcement';
import announcementsApi from '../../services/announcements-api';

type BreakdownMetric = {
  key: string;
  label: string;
  fill: string;
};

type BreakdownCardConfig = {
  id: 'status' | 'type' | 'cta';
  title: string;
  icon: 'status' | 'type' | 'cta';
  metrics: BreakdownMetric[];
};

const BREAKDOWN_CARDS: BreakdownCardConfig[] = [
  {
    id: 'status',
    title: 'Status',
    icon: 'status',
    metrics: [
      { key: 'live', label: 'Live', fill: '#0E9F6E' },
      { key: 'scheduled', label: 'Scheduled', fill: '#FEC080' },
      { key: 'inactive', label: 'Inactive', fill: '#827599' },
      { key: 'expired', label: 'Expired', fill: '#F05252' },
    ],
  },
  {
    id: 'type',
    title: 'Type',
    icon: 'type',
    metrics: [
      { key: 'image', label: 'Image', fill: '#F05252' },
      { key: 'video', label: 'Video', fill: '#FEC080' },
    ],
  },
  {
    id: 'cta',
    title: 'CTA Button',
    icon: 'cta',
    metrics: [
      { key: 'with_cta', label: 'With CTA', fill: '#5B31AA' },
      { key: 'no_cta', label: 'No CTA', fill: '#3F83F8' },
    ],
  },
];

const METRIC_ROW_MIN_HEIGHT = 'min-h-7';
const MAX_METRIC_ROWS = Math.max(
  ...BREAKDOWN_CARDS.map((card) => card.metrics.length)
);

function normKey(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, '_');
}

function findBreakdownItem(
  items: TAnnouncementStatsBreakdownItem[] | undefined,
  key: string
): TAnnouncementStatsBreakdownItem | undefined {
  if (!items?.length) return undefined;
  const want = normKey(key);
  return items.find(
    (item) => normKey(item.key) === want || normKey(item.label) === want
  );
}

function metricValue(
  items: TAnnouncementStatsBreakdownItem[] | undefined,
  key: string
): number {
  return findBreakdownItem(items, key)?.count ?? 0;
}

function metricPercentage(
  items: TAnnouncementStatsBreakdownItem[] | undefined,
  key: string,
  total: number,
  value: number
): number {
  const fromApi = findBreakdownItem(items, key)?.percentage;
  if (fromApi != null) return Math.round(fromApi);
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

function BreakdownIcon({ kind }: { kind: BreakdownCardConfig['icon'] }) {
  if (kind === 'status') {
    return (
      <span className='inline-flex size-5 items-center justify-center rounded-lg text-gray-900'>
        <Flash
          size={20}
          variant='Bold'
        />
      </span>
    );
  }
  if (kind === 'type') {
    return (
      <span className='inline-flex size-5 items-center justify-center rounded-lg text-gray-900'>
        <VideoHorizontal
          size={20}
          variant='Bold'
        />
      </span>
    );
  }
  return (
    <span className='inline-flex size-5 items-center justify-center rounded-lg text-gray-900'>
      <MouseCircle
        size={20}
        variant='Bold'
      />
    </span>
  );
}

function SemiDonut({
  data,
  total,
}: {
  data: { name: string; value: number; fill: string }[];
  total: number;
}) {
  const slices = data.filter((d) => d.value > 0);

  if (total <= 0 || slices.length === 0) {
    return (
      <div className='relative mx-auto flex h-[145px] w-full max-w-[280px] items-center justify-center pb-2'>
        <p className=' text-gray-500'>No announcements</p>
      </div>
    );
  }

  return (
    <div className='relative mx-auto h-[145px] w-full max-w-[280px]'>
      <ChartContainer
        style={{ height: '110px', width: '100%' }}
        config={{}}
      >
        <PieChart margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent />}
          />
          <Pie
            data={slices}
            dataKey='value'
            nameKey='name'
            cx='50%'
            cy='100%'
            startAngle={180}
            endAngle={0}
            innerRadius={72}
            outerRadius={100}
            paddingAngle={1}
            cornerRadius={4}
            stroke='#ffffff'
            strokeWidth={2}
          >
            {slices.map((entry) => (
              <Cell
                key={entry.name}
                fill={entry.fill}
              />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
      <div className='pointer-events-none absolute inset-x-0 bottom-8 flex flex-col items-center text-center'>
        <p className='text-base font-semibold tabular-nums text-gray-800'>
          {total.toLocaleString()}
        </p>
        <Text className='text-[11px] max-w-[120px] truncate text-center font-medium uppercase tracking-wide text-gray-600'>
          Total Announcements
        </Text>
      </div>
    </div>
  );
}

function BreakdownSummaryCard({
  config,
  items,
  total,
}: {
  config: BreakdownCardConfig;
  items?: TAnnouncementStatsBreakdownItem[];
  total: number;
}) {
  const pieSlices = config.metrics.map((m) => ({
    name: m.label,
    value: metricValue(items, m.key),
    fill: m.fill,
  }));

  const emptyMetricRows = MAX_METRIC_ROWS - config.metrics.length;

  return (
    <Card className='flex h-full min-h-0 w-full flex-col rounded-2xl border border-primary-100 bg-white shadow-sm'>
      <CardHeader className='px-5 pb-2 pt-5'>
        <div className='flex items-center gap-1'>
          <BreakdownIcon kind={config.icon} />
          <p className='text-[22px] font-semibold text-gray-900'>
            {config.title}
          </p>
        </div>
      </CardHeader>
      <CardContent className='flex flex-1 flex-col gap-4 px-5 pb-5 pt-0'>
        <SemiDonut
          data={pieSlices}
          total={total}
        />
        <div className='mt-auto flex flex-col gap-2.5 border-t border-primary-100 pt-3'>
          {config.metrics.map((m) => {
            const value = metricValue(items, m.key);
            const pct = metricPercentage(items, m.key, total, value);
            return (
              <div
                key={m.key}
                className={`flex ${METRIC_ROW_MIN_HEIGHT} items-center justify-between gap-2 text-sm`}
              >
                <div className='flex min-w-0 items-center gap-2'>
                  <span
                    className='h-[13px] w-[27px] shrink-0 rounded-[20px]'
                    style={{ backgroundColor: m.fill }}
                  />
                  <span className='truncate font-medium text-gray-1000'>
                    {m.label}
                  </span>
                </div>
                <span className='shrink-0 tabular-nums font-semibold text-gray-1000'>
                  {value.toLocaleString()}{' '}
                  <span className='font-medium text-gray-600'>({pct}%)</span>
                </span>
              </div>
            );
          })}
          {Array.from({ length: emptyMetricRows }).map((_, index) => (
            <div
              key={`metric-spacer-${index}`}
              className={METRIC_ROW_MIN_HEIGHT}
              aria-hidden
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function AnnouncementOverviewBreakdownCards() {
  const orgId = useGetOrgId();
  const { data, isLoading } = announcementsApi.useGetStats(orgId);
  const total = data?.total_announcements ?? 0;

  if (isLoading) {
    return (
      <div className='grid w-full grid-cols-1 gap-6 md:grid-cols-3'>
        {[0, 1, 2].map((i) => (
          <Card
            key={i}
            className='flex h-[420px] flex-col rounded-2xl border border-primary-100 bg-white p-4'
          >
            <SkeletonLoader height={360} />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className='grid w-full grid-cols-1 items-stretch gap-4 md:grid-cols-3'>
      {BREAKDOWN_CARDS.map((cfg) => (
        <BreakdownSummaryCard
          key={cfg.id}
          config={cfg}
          items={data?.[cfg.id]}
          total={total}
        />
      ))}
    </div>
  );
}
